import {
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
  AppRegistration as AssignProgramIcon,
} from '@mui/icons-material';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { TASK_TYPES } from 'src/careflow/helpers/constants';
import { MenuTaskHandler } from 'src/careflow/helpers/types';
import {
  getAssessmentFromTask,
  getAssessmentUrlWithUserId,
  isAssessmentReadyToStart,
} from 'src/careflow/helpers/common';
import Iconify from 'src/components/Iconify';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';

export const assessmentHandler: MenuTaskHandler = {
  icon: <AssignmentIcon />,
  taskType: TASK_TYPES.ASSESSMENT,
  labelString: (i18n: i18nFunction) => i18n('tasks.menuTaskHandler.assessment', 'crs'),
  conditionToShow: (task) => {
    const inputAssessment = getAssessmentFromTask(task);
    return !!inputAssessment;
  },
  handler: (task, externalHandler) => {
    if (externalHandler) {
      return externalHandler(task);
    }

    const inputAssessment = getAssessmentFromTask(task);
    if (!inputAssessment) return;

    if (isAssessmentReadyToStart(inputAssessment)) {
      const assessmentUrl = getAssessmentUrlWithUserId(inputAssessment, undefined, {
        taskId: task?.id,
        patientId: task?.getPatientId?.(),
      });
      window.open(assessmentUrl, '_blank');
    }
  },
};

export const NATHandler: MenuTaskHandler = {
  icon: <OpenInNewIcon />,
  taskType: TASK_TYPES.NAT,
  labelString: (i18n: i18nFunction) => i18n('tasks.menuTaskHandler.natDetail', 'crs'),
  conditionToShow: (task) => task?.getTaskType() === TASK_TYPES.NAT,
  handler: (task) => {
    window.open(`${PATH_DASHBOARD.nat.task}/${task?.id}`, '_blank');
  },
};

export const assignProgramHandler: MenuTaskHandler = {
  icon: <AssignProgramIcon />,
  taskType: TASK_TYPES.PROGRAM,
  labelString: (i18n: i18nFunction) => i18n('tasks.menuTaskHandler.assignProgram', 'crs'),
  conditionToShow: (task, permission) => {
    const validatePermission = permission ?? true;
    return validatePermission && task?.getTaskType() === TASK_TYPES.PROGRAM;
  },
  handler: (task, externalHandler) => {
    if (externalHandler) return externalHandler(task);
  },
};

export const referralInternalHandler: MenuTaskHandler = {
  icon: <Iconify icon="lets-icons:group-share" height={20} width={20} />,
  taskType: TASK_TYPES.REFERRAL_INTERNAL,
  labelString: (i18n: i18nFunction) => i18n('tasks.menuTaskHandler.serviceRequest', 'crs'),
  conditionToShow: (task, permission) => {
    const validatePermission = permission ?? true;
    return validatePermission && task?.getTaskType() === TASK_TYPES.REFERRAL_INTERNAL;
  },
  handler: (task, externalHandler) => {
    if (externalHandler) return externalHandler(task);
  },
};

export const referralExternalHandler: MenuTaskHandler = {
  icon: <Iconify icon="lets-icons:group-share" height={20} width={20} />,
  taskType: TASK_TYPES.REFERRAL_EXTERNAL,
  labelString: (i18n: i18nFunction) => i18n('tasks.menuTaskHandler.serviceRequest', 'crs'),
  conditionToShow: (task, permission) => {
    const validatePermission = permission ?? true;
    return validatePermission && task?.getTaskType() === TASK_TYPES.REFERRAL_EXTERNAL;
  },
  handler: (task, externalHandler) => {
    if (externalHandler) return externalHandler(task);
  },
};

export const appointmentSchedulingHandler: MenuTaskHandler = {
  icon: <Iconify icon="mdi:calendar-outline" height={20} width={20} />,
  taskType: TASK_TYPES.APPOINTMENT_SCHEDULING,
  labelString: (i18n: i18nFunction) => i18n('tasks.menuTaskHandler.appointment', 'crs'),
  conditionToShow: (task, permission) => {
    const validatePermission = permission ?? true;
    return validatePermission && task?.getTaskType() === TASK_TYPES.APPOINTMENT_SCHEDULING;
  },
  handler: (task, externalHandler) => {
    if (externalHandler) return externalHandler(task);
  },
};

export const warmHandoffHandler: MenuTaskHandler = {
  icon: <Iconify icon="lets-icons:group-share" height={20} width={20} />,
  taskType: TASK_TYPES.WARM_HANDOFF,
  labelString: (i18n: i18nFunction) => i18n('tasks.menuTaskHandler.serviceRequest', 'crs'),
  conditionToShow: (task, permission) => {
    const validatePermission = permission ?? true;
    return validatePermission && task?.getTaskType() === TASK_TYPES.WARM_HANDOFF;
  },
  handler: (task, externalHandler) => {
    if (externalHandler) return externalHandler(task);
  },
};

export const commonMenuTaskHandlers: MenuTaskHandler[] = [
  assessmentHandler,
  NATHandler,
  assignProgramHandler,
  referralInternalHandler,
  referralExternalHandler,
  appointmentSchedulingHandler,
  warmHandoffHandler,
];
